import { useState, useEffect } from 'react'
import { Plus, Search, Pencil, Trash2, X, AlertTriangle, BoxSelect, BookmarkCheck, Minus } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast, ConfirmModal } from '../../../components/Toast'
import { Articolo, ArticoloForm, Fornitore, TipoArticolo, TIPI_ARTICOLO, TIPI_ARTICOLO_LABELS, PrenotazioneArticolo } from '../../../types/magazzino'

const emptyForm: ArticoloForm = {
  codice: '',
  descrizione: '',
  tipo: 'materia_prima',
  unita_misura: 'pz',
  prezzo_acquisto: '0',
  fornitore: '',
  fornitore_id: '',
  scorta_minima: '0',
  giacenza_attuale: '0',
  listino_id: '',
  ubicazione: '',
  note: '',
  attivo: true,
}

const TIPO_COLORS: Record<TipoArticolo, string> = {
  materia_prima: 'bg-amber-100 text-amber-700',
  semilavorato: 'bg-blue-100 text-blue-700',
  prodotto_finito: 'bg-green-100 text-green-700',
}

export default function ArticoliPage() {
  const [articoli, setArticoli] = useState<Articolo[]>([])
  const [fornitori, setFornitori] = useState<Fornitore[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterTipo, setFilterTipo] = useState<string>('')

  const [showModal, setShowModal] = useState(false)
  const [editingArticolo, setEditingArticolo] = useState<Articolo | null>(null)
  const [form, setForm] = useState<ArticoloForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Articolo | null>(null)

  // Prenotazioni
  const [showPrenotazioni, setShowPrenotazioni] = useState(false)
  const [prenotazioniArticolo, setPrenotazioniArticolo] = useState<Articolo | null>(null)
  const [prenotazioni, setPrenotazioni] = useState<PrenotazioneArticolo[]>([])
  const [loadingPrenotazioni, setLoadingPrenotazioni] = useState(false)
  const [editingPrenotazione, setEditingPrenotazione] = useState<string | null>(null)
  const [editQta, setEditQta] = useState('')
  const [savingPrenotazione, setSavingPrenotazione] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [artRes, fornRes] = await Promise.all([
        api.get('/v1/magazzino/articoli'),
        api.get('/v1/magazzino/fornitori?attivo=true'),
      ])
      setArticoli(artRes.data)
      setFornitori(fornRes.data)
    } catch {
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const filteredArticoli = articoli.filter(a => {
    const matchSearch = !search ||
      a.codice.toLowerCase().includes(search.toLowerCase()) ||
      a.descrizione.toLowerCase().includes(search.toLowerCase()) ||
      (a.fornitore_nome?.toLowerCase() || '').includes(search.toLowerCase()) ||
      (a.fornitore?.toLowerCase() || '').includes(search.toLowerCase())
    const matchTipo = !filterTipo || a.tipo === filterTipo
    return matchSearch && matchTipo
  })

  const handleCreate = () => {
    setEditingArticolo(null)
    setForm(emptyForm)
    setError('')
    setShowModal(true)
  }

  const handleEdit = (articolo: Articolo) => {
    setEditingArticolo(articolo)
    setForm({
      codice: articolo.codice,
      descrizione: articolo.descrizione,
      tipo: articolo.tipo,
      unita_misura: articolo.unita_misura,
      prezzo_acquisto: String(articolo.prezzo_acquisto || 0),
      fornitore: articolo.fornitore || '',
      fornitore_id: articolo.fornitore_id || '',
      scorta_minima: String(articolo.scorta_minima || 0),
      giacenza_attuale: String(articolo.giacenza_attuale || 0),
      listino_id: articolo.listino_id || '',
      ubicazione: articolo.ubicazione || '',
      note: articolo.note || '',
      attivo: articolo.attivo,
    })
    setError('')
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    try {
      const data: any = {
        codice: form.codice.trim(),
        descrizione: form.descrizione.trim(),
        tipo: form.tipo,
        unita_misura: form.unita_misura.trim() || 'pz',
        prezzo_acquisto: parseFloat(form.prezzo_acquisto) || 0,
        fornitore_id: form.fornitore_id || null,
        fornitore: form.fornitore_id ? null : (form.fornitore.trim() || null),
        scorta_minima: parseFloat(form.scorta_minima) || 0,
        giacenza_attuale: parseFloat(form.giacenza_attuale) || 0,
        listino_id: form.listino_id || null,
        ubicazione: form.ubicazione.trim() || null,
        note: form.note.trim() || null,
        attivo: form.attivo,
      }

      if (editingArticolo) {
        await api.put(`/v1/magazzino/articoli/${editingArticolo.id}`, data)
        setToast({ message: 'Articolo modificato', type: 'success' })
      } else {
        await api.post('/v1/magazzino/articoli', data)
        setToast({ message: 'Articolo creato', type: 'success' })
      }

      setShowModal(false)
      loadData()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return
    try {
      await api.delete(`/v1/magazzino/articoli/${confirmDelete.id}`)
      setToast({ message: 'Articolo eliminato', type: 'success' })
      loadData()
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore nella cancellazione', type: 'error' })
    } finally {
      setConfirmDelete(null)
    }
  }

  const handleShowPrenotazioni = async (articolo: Articolo) => {
    setPrenotazioniArticolo(articolo)
    setShowPrenotazioni(true)
    setLoadingPrenotazioni(true)
    setEditingPrenotazione(null)
    try {
      const res = await api.get(`/v1/magazzino/articoli/${articolo.id}/prenotazioni`)
      setPrenotazioni(res.data)
    } catch {
      setToast({ message: 'Errore nel caricamento prenotazioni', type: 'error' })
    } finally {
      setLoadingPrenotazioni(false)
    }
  }

  const handleUpdatePrenotazione = async (prenotazioneId: string) => {
    const nuovaQta = parseFloat(editQta)
    if (isNaN(nuovaQta) || nuovaQta < 0) return
    setSavingPrenotazione(true)
    try {
      await api.put(`/v1/magazzino/prenotazioni/${prenotazioneId}`, { quantita: nuovaQta })
      setToast({ message: nuovaQta === 0 ? 'Prenotazione eliminata' : 'Prenotazione aggiornata', type: 'success' })
      setEditingPrenotazione(null)
      // Ricarica prenotazioni e articoli
      if (prenotazioniArticolo) {
        const res = await api.get(`/v1/magazzino/articoli/${prenotazioniArticolo.id}/prenotazioni`)
        setPrenotazioni(res.data)
        if (res.data.length === 0) setShowPrenotazioni(false)
      }
      loadData()
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore aggiornamento', type: 'error' })
    } finally {
      setSavingPrenotazione(false)
    }
  }

  const getScortaIndicator = (a: Articolo) => {
    const giacenza = a.giacenza_attuale || 0
    const scorta = a.scorta_minima || 0
    if (giacenza <= 0) return { color: 'text-red-600', bg: 'bg-red-50', label: 'Esaurito' }
    if (giacenza < scorta) return { color: 'text-amber-600', bg: 'bg-amber-50', label: 'Sotto scorta' }
    return { color: 'text-green-600', bg: 'bg-green-50', label: 'OK' }
  }

  const getFornitoreDisplay = (a: Articolo) => {
    if (a.fornitore_nome) return a.fornitore_nome
    if (a.fornitore) return a.fornitore
    return '-'
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Articoli Magazzino</h1>
          <p className="text-gray-500">Anagrafica materie prime, semilavorati e prodotti finiti</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-5 h-5" />
          Nuovo Articolo
        </button>
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per codice, descrizione, fornitore..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterTipo('')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                !filterTipo ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Tutti ({articoli.length})
            </button>
            {TIPI_ARTICOLO.map(tipo => (
              <button
                key={tipo}
                onClick={() => setFilterTipo(tipo === filterTipo ? '' : tipo)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterTipo === tipo ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {TIPI_ARTICOLO_LABELS[tipo]} ({articoli.filter(a => a.tipo === tipo).length})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : filteredArticoli.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <BoxSelect className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Nessun articolo trovato</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Codice</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrizione</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Giacenza</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Scorta Min.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fornitore</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ubicazione</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredArticoli.map(a => {
                const scorta = getScortaIndicator(a)
                return (
                  <tr key={a.id} className={`hover:bg-gray-50 ${!a.attivo ? 'opacity-50' : ''}`}>
                    <td className="px-6 py-4 font-mono text-sm font-medium text-gray-900">{a.codice}</td>
                    <td className="px-6 py-4 text-gray-900">{a.descrizione}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${TIPO_COLORS[a.tipo]}`}>
                        {TIPI_ARTICOLO_LABELS[a.tipo]}
                      </span>
                    </td>
                    <td className={`px-6 py-4 text-right font-medium ${scorta.color}`}>
                      {a.giacenza_attuale} {a.unita_misura}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-500">
                      {a.scorta_minima} {a.unita_misura}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${scorta.bg} ${scorta.color}`}>
                        {scorta.label === 'Sotto scorta' && <AlertTriangle className="w-3 h-3" />}
                        {scorta.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{getFornitoreDisplay(a)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{a.ubicazione || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleShowPrenotazioni(a)} className="p-2 text-gray-400 hover:text-teal-600" title="Prenotazioni">
                          <BookmarkCheck className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleEdit(a)} className="p-2 text-gray-400 hover:text-primary-600" title="Modifica">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => setConfirmDelete(a)} className="p-2 text-gray-400 hover:text-red-600" title="Elimina">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Crea/Modifica */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingArticolo ? 'Modifica Articolo' : 'Nuovo Articolo'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Codice *</label>
                  <input
                    type="text"
                    required
                    value={form.codice}
                    onChange={(e) => setForm({ ...form, codice: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="ART-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                  <select
                    required
                    value={form.tipo}
                    onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {TIPI_ARTICOLO.map(t => (
                      <option key={t} value={t}>{TIPI_ARTICOLO_LABELS[t]}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione *</label>
                <input
                  type="text"
                  required
                  value={form.descrizione}
                  onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Descrizione articolo"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unita' di misura</label>
                  <select
                    value={form.unita_misura}
                    onChange={(e) => setForm({ ...form, unita_misura: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="pz">pz</option>
                    <option value="kg">kg</option>
                    <option value="m">m</option>
                    <option value="mq">mq</option>
                    <option value="mc">mc</option>
                    <option value="lt">lt</option>
                    <option value="set">set</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Acquisto</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.prezzo_acquisto}
                    onChange={(e) => setForm({ ...form, prezzo_acquisto: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scorta Minima</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.scorta_minima}
                    onChange={(e) => setForm({ ...form, scorta_minima: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {!editingArticolo && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Giacenza Iniziale</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.giacenza_attuale}
                      onChange={(e) => setForm({ ...form, giacenza_attuale: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fornitore</label>
                  <select
                    value={form.fornitore_id}
                    onChange={(e) => setForm({ ...form, fornitore_id: e.target.value, fornitore: '' })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">— Nessuno —</option>
                    {fornitori.map(f => (
                      <option key={f.id} value={f.id}>{f.codice} — {f.ragione_sociale}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ubicazione</label>
                  <input
                    type="text"
                    value={form.ubicazione}
                    onChange={(e) => setForm({ ...form, ubicazione: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Es. Scaffale A3"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {editingArticolo && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="attivo"
                    checked={form.attivo}
                    onChange={(e) => setForm({ ...form, attivo: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="attivo" className="text-sm text-gray-700">Attivo</label>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Confirm Delete */}
      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Elimina Articolo"
        message={`Sei sicuro di voler eliminare l'articolo "${confirmDelete?.codice} - ${confirmDelete?.descrizione}"?`}
        confirmText="Elimina"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(null)}
      />

      {/* Modal Prenotazioni */}
      {showPrenotazioni && prenotazioniArticolo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Prenotazioni Articolo</h2>
                <p className="text-sm text-gray-500 mt-1">
                  <span className="font-mono">{prenotazioniArticolo.codice}</span> — {prenotazioniArticolo.descrizione}
                </p>
              </div>
              <button onClick={() => setShowPrenotazioni(false)} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {loadingPrenotazioni ? (
                <div className="text-center text-gray-500 py-8">Caricamento...</div>
              ) : prenotazioni.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <BookmarkCheck className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p>Nessuna prenotazione attiva per questo articolo</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Offerta</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qta</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Stato</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {prenotazioni.map(p => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-sm text-primary-600">{p.numero_offerta}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{p.cliente_nome}</td>
                        <td className="px-4 py-3 text-right">
                          {editingPrenotazione === p.id ? (
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max={p.quantita}
                              value={editQta}
                              onChange={(e) => setEditQta(e.target.value)}
                              className="w-20 px-2 py-1 text-right border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                              autoFocus
                            />
                          ) : (
                            <span className="font-medium text-gray-900">{p.quantita} {prenotazioniArticolo.unita_misura}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {p.disponibilita_ok ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700">OK</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700">
                              <AlertTriangle className="w-3 h-3" /> Insufficiente
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {editingPrenotazione === p.id ? (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleUpdatePrenotazione(p.id)}
                                disabled={savingPrenotazione}
                                className="px-2 py-1 bg-teal-600 text-white rounded text-xs hover:bg-teal-700 disabled:opacity-50"
                              >
                                {savingPrenotazione ? '...' : 'Salva'}
                              </button>
                              <button
                                onClick={() => setEditingPrenotazione(null)}
                                className="px-2 py-1 text-gray-500 hover:text-gray-700 text-xs"
                              >
                                Annulla
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setEditingPrenotazione(p.id); setEditQta(String(p.quantita)) }}
                              className="p-1.5 text-gray-400 hover:text-teal-600"
                              title="Modifica quantita'"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
